import { TrackingProviders } from '@common/order/types';
import { SearchParams } from '@solidjs/router';
import { ltlQuoteState } from '@store/ltl/store';
import {
  carrierStore,
  featureFlagGetCustomerFeatures,
  LtlQuoteLoadItemViewModel,
  orderStore,
  PickUpDropOffItem,
} from '@store/orders';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { getEligibleEntries, isAdmin, sdbm } from '@utils/utils';
import { each, get, isEmpty, isEqual } from 'lodash';

export const refundAdjustmentError = (
  retractionCount: number,
  refundCount: number,
) => {
  return refundCount - retractionCount > 0;
};

export const hasQualifiedStatus = (status: string | undefined) => {
  if (!status) {
    return false;
  }

  return ['Can Get', 'Unassigned', 'Assigned'].includes(status);
};

export const retractionAdjustmentError = (
  retractionCount: number,
  refundCount: number,
) => {
  return retractionCount - refundCount > 1;
};

export const setCustomerCommentVisiblityForComponent = () => {
  if (
    orderStore.order.loads[orderStore.activeTab.index].mode == 'LTL' ||
    get(
      featureFlagGetCustomerFeatures,
      'featureFlagCustomer.CustomerCommentVisibility',
    )
  ) {
    return false;
  }
  return true;
};

export const isStopValid = (stops: LastUsedTopStopModel[]) => {
  let hasPickup = false;
  let hasDropoff = false;
  let hasDateTime = true;
  let firstValidStop;
  for (const stop of stops) {
    if (stop.operationType === 'Delete') continue;
    if (!firstValidStop) firstValidStop = stop;
    if (!Boolean(stop.stopDateTime)) hasDateTime = false;
    if (stop.pickUp) hasPickup = true;
    if (stop.dropOff) hasDropoff = true;
  }

  if (!hasPickup && !hasDropoff) {
    return 'You need at least one pick up and one drop off';
  } else if (!hasPickup) {
    return 'You need at least one pick up';
  } else if (!hasDropoff) {
    return 'You need at least one drop off';
  }
  if (!hasDateTime) {
    return 'All stops must have a stop date time';
  }
  if (firstValidStop && !firstValidStop.pickUp) {
    return 'First Stop should be Pickup';
  }

  return '';
};

export const checkTLModeError = (
  load: ILoadEditViewModel,
  carrierIndex: number,
) => {
  const disallowedStatuses = ['Void', 'Billed', 'Paid', 'Closed'];

  const err = [];

  if (
    !load.cargoValueIsHigh! &&
    !isAdmin() &&
    !disallowedStatuses.includes(load.status!)
  ) {
    err.push('Indicate if the cargo value is more than $100,000');
  }

  if (load.cargoValueIsHigh! === 'No' && load.freightValue! > 100000) {
    err.push('Enter cargo value: 0- $100,000.');
  }
  if (
    load.cargoValueIsHigh! === 'Yes' &&
    !load.enableLoadCarrierRestrictionOverride &&
    load.freightValue! > 100000 &&
    carrierStore.carrier.length > 0 &&
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    carrierStore.carrier[carrierIndex] &&
    carrierStore.carrier[carrierIndex].totalLoads <= 3
  ) {
    err.push(
      'Load cannot be booked because the carrier has a load history of three loads or less and the value of the load is $100,000 or greater and they should contact Carrier Relations to override the booking if they want to proceed with the selected carrier',
    );
  }
  return err;
};

export const checkZipForChanges = (
  load: ILoadEditViewModel,
  eligibleStops: LastUsedTopStopModel[],
) => {
  if (!Boolean(load.ltlQuote?.quoteId) || !load.ltlQuote) return false;
  const normalizeZip = (zip: string | undefined): string | undefined =>
    zip?.trim().replace(/\s/g, '').toUpperCase().split('-')[0];

  let originZip: string | undefined;
  let destinationZip: string | undefined;
  if (load.ltlQuoteParameters) {
    originZip = normalizeZip(load.ltlQuoteParameters.originZip);
    destinationZip = normalizeZip(load.ltlQuoteParameters.destinationZip);
  } else if (ltlQuoteState.quote) {
    originZip = normalizeZip(ltlQuoteState.quote.originZip);
    destinationZip = normalizeZip(ltlQuoteState.quote.destinationZip);
  }

  const eligibleOriginStop = eligibleStops.find((x) => x.pickUp);
  const stopOriginZip = normalizeZip(eligibleOriginStop?.zip);

  if (stopOriginZip !== originZip) {
    return true;
  }

  const eligibleDestinationStop = eligibleStops.find((x) => !x.pickUp);
  const stopDestinationZip = normalizeZip(eligibleDestinationStop?.zip);

  if (stopDestinationZip !== destinationZip) {
    return true;
  }

  return false;
};

const reduceItemsToHash = (
  hashes: Record<number, number>,
  item: PickUpDropOffItem | LtlQuoteLoadItemViewModel,
) => {
  const str = [item.quantity, item.weight, item.class].join(',');
  const hash = sdbm(str);

  if (hash in hashes) {
    hashes[hash]++;
  } else {
    hashes[hash] = 1;
  }

  return hashes;
};

const hasStopChanges = (load: ILoadEditViewModel) => {
  let quoteChanges = false;
  const stopChanges = false;
  let zipChanges = false;
  const eligibleItems = getEligibleEntries<PickUpDropOffItem>(load.items);
  const quoteItems = load.ltlQuote?.ltlQuoteLoadItems ?? [];

  if (quoteItems.length > 0 && quoteItems.length == eligibleItems.length) {
    if (eligibleItems.length !== quoteItems.length) {
      quoteChanges = true;
    } else {
      const eligibleItemsHashes = eligibleItems.reduce(
        reduceItemsToHash,
        {} as Record<number, number>,
      );

      const quotedItemsHashes = quoteItems.reduce(
        reduceItemsToHash,
        {} as Record<number, number>,
      );

      quoteChanges = !isEqual(eligibleItemsHashes, quotedItemsHashes);
    }
  } else {
    quoteChanges = true;
  }

  const eligibleStops = getEligibleEntries<LastUsedTopStopModel>(load.stops);
  if (!quoteChanges) {
    const zipForChanges = checkZipForChanges(load, eligibleStops);
    zipChanges = zipForChanges;
  }

  return {
    quoteChanges: quoteChanges,
    stopChanges: stopChanges,
    zipChanges: zipChanges,
  };
};

export const checkLTLModeError = (load: ILoadEditViewModel) => {
  const err: string[] = [];
  const eligibleStops = getEligibleEntries<LastUsedTopStopModel>(load.stops);

  const deliveryWindowsValid = () => {
    let valid = true;
    each(eligibleStops, (stop) => {
      if (!stop.driverInTime! || !stop.driverOutTime!) {
        valid = false;
      }
    });
    return valid;
  };

  const quoteItems = load.ltlQuote?.ltlQuoteLoadItems;

  if (
    !isEmpty(load.ltlquoteId) &&
    !isEmpty(quoteItems) &&
    !isEmpty(eligibleStops)
  ) {
    if (!deliveryWindowsValid()) {
      err.push(
        'Pickup and delivery windows are not set. These can be set by entering Driver In/Driver Out times on stops.',
      );
    }
    // Per Mike 11/8/2021, we are not going to check for item changes until we have a better understanding of how to handle them.
    // if (hasStopChanges(load).quoteChanges) {
    //   err.push(
    //     'Item changes have been made on this load, please confirm the item quantity, weight and class are the same as originally quoted and re-quote the load.',
    //   );
    // }

    if (hasStopChanges(load).stopChanges) {
      err.push(
        'Stops have incomplete address information. Please add necessary address details.',
      );
    }
  }

  return err;
};

export const ltlLoadHasItemDescription = (load: ILoadEditViewModel) => {
  const eligibleItems = getEligibleEntries<PickUpDropOffItem>(load.items);
  return eligibleItems.every((item) => item.description.length > 0);
};

export const isValidEmailChain = (emails: string): boolean => {
  if (emails.trim() === '') return true;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emails
    .split(',')
    .map((e) => e.trim())
    .filter((e) => e !== '')
    .every((e) => emailRegex.test(e));
};

export const getTrackingProviderLabel = (
  value: number | undefined,
): string | undefined => {
  for (const key in TrackingProviders) {
    if (TrackingProviders[key as keyof typeof TrackingProviders] === value) {
      return key;
    }
  }
  return undefined;
};

export const getSearchParams = () => {
  const q = new URL(window.location.href).searchParams;
  const query = (
    Array.from(q.entries()) as unknown as [string, string][]
  ).reduce((acc, [k, v]) => {
    return {
      ...acc,
      [k]: v,
    };
  }, {} as SearchParams);

  return query;
};
